import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getStorageConfiguration,
  updateStorageConfiguration,
  testStorageConnection,
  browseDirectories,
  createDirectory,
  getWorkerEffectiveStorage,
} from "../api/workerApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero } from "../components/adminSurface/AdminSurface";
import { ServerArt } from "../components/adminSurface/heroArt";

/**
 * Says which layer an effective value came from.
 *
 * "database" is the shared setting every node resolves; "file" means nothing has been saved
 * centrally yet and this one machine's appsettings.json is still deciding. The distinction is the
 * whole point of the screen: for years it showed a value with no indication that the worker doing
 * the work had never read it.
 */
function SourceBadge({ source, t }) {
  const styles = {
    database: { bg: "#dcfce7", fg: "#166534" },
    file: { bg: "#fef3c7", fg: "#92400e" },
    unset: { bg: "#f3f4f6", fg: "#4b5563" },
  };
  const label = {
    database: t("storage.sourceShared", { defaultValue: "Shared setting" }),
    file: t("storage.sourceHostFile", { defaultValue: "This server's file only" }),
    unset: t("storage.sourceUnset", { defaultValue: "Not configured" }),
  };
  const tone = styles[source] || styles.unset;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 4,
        backgroundColor: tone.bg,
        color: tone.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label[source] || label.unset}
    </span>
  );
}

export default function StorageConfiguration() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [config, setConfig] = useState({
    mode: "FileSystem",
    batchWorkingRoot: "",
    azureBlobConnectionString: "",
    azureMainRoot: "",
    azureStagingRoot: "",
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [browserPath, setBrowserPath] = useState(null);
  const [browserEntries, setBrowserEntries] = useState([]);
  const [browserParent, setBrowserParent] = useState(null);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserError, setBrowserError] = useState("");
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");
  // Who actually obeys the value on this page. Loaded alongside it, because a setting screen that
  // cannot show which nodes use the setting is how the original defect stayed invisible for years.
  const [workers, setWorkers] = useState(null);
  const [workersError, setWorkersError] = useState("");
  const [workersOpen, setWorkersOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchConfiguration();
    fetchWorkers();
  }, [token]);

  const openBrowser = async () => {
    setBrowserOpen(true);
    setBrowserError("");
    await navigateTo(config.batchWorkingRoot || null);
  };

  const navigateTo = async (path) => {
    setBrowserLoading(true);
    setBrowserError("");
    try {
      const data = await browseDirectories(token, path);
      setBrowserPath(data.currentPath);
      setBrowserParent(data.parent);
      setBrowserEntries(data.directories || []);
    } catch (err) {
      setBrowserError(err.message || "Could not browse directory");
    } finally {
      setBrowserLoading(false);
    }
  };

  const selectBrowserPath = (path) => {
    setConfig({ ...config, batchWorkingRoot: path });
    setBrowserOpen(false);
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setNewFolderError("Enter a folder name"); return; }
    if (!browserPath) { setNewFolderError("Navigate into a folder first"); return; }
    setNewFolderError("");
    try {
      const result = await createDirectory(token, browserPath, name);
      setNewFolderMode(false);
      setNewFolderName("");
      await navigateTo(result.path);
    } catch (err) {
      setNewFolderError(err.message || "Could not create folder");
    }
  };

  const fetchConfiguration = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getStorageConfiguration(token);
      setConfig(data || config);
      setOriginalConfig(data || config);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    setWorkersError("");
    try {
      setWorkers(await getWorkerEffectiveStorage(token));
    } catch (err) {
      // Non-fatal: the page still configures storage. But say so rather than rendering an empty
      // table, which would read as "no worker uses this" — the opposite of the truth.
      setWorkers(null);
      setWorkersError(friendlyMessageForApiError(err));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await updateStorageConfiguration(token, config);
      setSuccess(
        t("storage.savedApplies", {
          seconds: result?.appliesWithinSeconds ?? config.refreshIntervalSeconds ?? 60,
          defaultValue:
            "Saved. Workers will be using it within {{seconds}} seconds — no restart needed.",
        })
      );
      // Re-read rather than assuming: the response tells us which layer now supplies each value,
      // and the freshly saved path may have been trimmed. Showing the stored truth beats echoing
      // back what was typed.
      await fetchConfiguration();
      await fetchWorkers();
      setTimeout(() => setSuccess(""), 6000);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const result = await testStorageConnection(token, config);
      setTestResult(result);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);
  // Workers that pin their own path and will therefore ignore whatever is saved above.
  const overrideCount = (workers?.workers ?? []).filter((w) => w.source === "override").length;

  return (
    <>
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />
      <div className="page-container">
        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3" />
              <rect x="3" y="8" width="18" height="6" rx="1.5" />
              <rect x="3" y="14" width="18" height="6" rx="1.5" />
              <line x1="7" y1="11" x2="7.01" y2="11" />
              <line x1="7" y1="17" x2="7.01" y2="17" />
            </svg>
          }
          title={t("storage.configuration", { defaultValue: "Storage Configuration" })}
          subtitle={t("storage.subtitle", {
            defaultValue:
              "Configure storage backend for batch processing (local file system or Azure Blob Storage)",
          })}
          art={<ServerArt />}
        />

        {loading && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 0",
              color: "#666",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                border: "3px solid #e0e0e0",
                borderTop: "3px solid #2563eb",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: 12 }}>Loading configuration...</p>
          </div>
        )}

        {!loading && (
          <div className="config-card">
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                Storage Mode
              </h2>
            </div>

            <div style={{ padding: 24 }}>
              {/* Storage Mode Selection */}
              <div style={{ marginBottom: 24 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    fontWeight: 500,
                    marginBottom: 12,
                  }}
                >
                  Select Storage Backend
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 16,
                  }}
                >
                  {/* Local File System Option */}
                  <div
                    onClick={() => setConfig({ ...config, mode: "FileSystem" })}
                    style={{
                      padding: 16,
                      border:
                        config.mode === "FileSystem"
                          ? "2px solid #3b82f6"
                          : "2px solid #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                      backgroundColor:
                        config.mode === "FileSystem" ? "#eff6ff" : "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="radio"
                        checked={config.mode === "FileSystem"}
                        onChange={() =>
                          setConfig({ ...config, mode: "FileSystem" })
                        }
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <h3
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            marginBottom: 6,
                          }}
                        >
                          Local File System
                        </h3>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                          Use local or network file storage (default for
                          on-premises)
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 10,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#d1fae5",
                              color: "#065f46",
                              borderRadius: 4,
                            }}
                          >
                            Fast
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              borderRadius: 4,
                            }}
                          >
                            On-Premises
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Azure Blob Storage Option */}
                  <div
                    onClick={() => setConfig({ ...config, mode: "AzureBlob" })}
                    style={{
                      padding: 16,
                      border:
                        config.mode === "AzureBlob"
                          ? "2px solid #3b82f6"
                          : "2px solid #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                      backgroundColor:
                        config.mode === "AzureBlob" ? "#eff6ff" : "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="radio"
                        checked={config.mode === "AzureBlob"}
                        onChange={() =>
                          setConfig({ ...config, mode: "AzureBlob" })
                        }
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <h3
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            marginBottom: 6,
                          }}
                        >
                          Azure Blob Storage
                        </h3>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                          Cloud storage for scalable, distributed deployments
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 10,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#f3e8ff",
                              color: "#6b21a8",
                              borderRadius: 4,
                            }}
                          >
                            Cloud
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#ffedd5",
                              color: "#9a3412",
                              borderRadius: 4,
                            }}
                          >
                            Scalable
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Local File System Configuration */}
              {config.mode === "FileSystem" && (
                <div
                  style={{
                    paddingTop: 24,
                    borderTop: "1px solid #e5e7eb",
                    marginTop: 24,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      marginBottom: 16,
                    }}
                  >
                    File System Settings
                  </h3>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 500,
                        marginBottom: 8,
                      }}
                    >
                      Batch Working Root Path *
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        value={config.batchWorkingRoot}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            batchWorkingRoot: e.target.value,
                          })
                        }
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontFamily: "monospace",
                          fontSize: 13,
                        }}
                        placeholder="/Users/username/DepositoryData  or  C:/DepositoryData"
                        // The shared settings column is VARCHAR(250); the server rejects longer,
                        // so stop it at the keyboard rather than at the save button.
                        maxLength={config.maxValueLength || 250}
                      />
                      <button
                        type="button"
                        onClick={openBrowser}
                        style={{
                          padding: "8px 14px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          backgroundColor: "#f9fafb",
                          cursor: "pointer",
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        📂 Browse
                      </button>
                    </div>
                    {/* One muted line, not four stacked warnings. The browse caveat belongs with the
                        field's own help text — it is the same sentence about the same path. */}
                    <p style={{ fontSize: 12, color: "#666", marginTop: 6, marginBottom: 0 }}>
                      {t("storage.batchRootPathHelp", {
                        defaultValue:
                          "Local directory path where batch files will be processed. The system will create 'process', 'success', and 'error' subfolders automatically.",
                      })}{" "}
                      {t("storage.browseScopeWarning", {
                        defaultValue:
                          "Browse lists folders on the API server. Make sure the path also exists on every worker machine — a UNC path (\\\\server\\share) is the safest choice.",
                      })}
                    </p>

                    {/* One status line: where this value comes from, and when it takes effect. The
                        stale-file note joins it only when the two actually disagree, which is the
                        one case worth interrupting someone for. */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 10,
                        fontSize: 12,
                      }}
                    >
                      <SourceBadge source={config.batchWorkingRootSource} t={t} />
                      {/* A value stored before the path was validated: the workers refuse it, so the
                          screen must not look content. Rendered next to the badge, in the one place
                          an admin is already reading. */}
                      {config.batchWorkingRootProblem ? (
                        <span style={{ color: "#991b1b", fontWeight: 600 }}>
                          {t(`storage.rootProblem.${config.batchWorkingRootProblem}`, {
                            defaultValue:
                              "This path is not usable — workers refuse it. Correct it and save.",
                          })}
                        </span>
                      ) : null}
                      {/* Suppressed while the path is refused: "workers apply this in 60 seconds"
                          next to "workers refuse it" is two contradictory sentences on one line. */}
                      {!config.batchWorkingRootProblem && (
                        <span style={{ color: "#166534" }}>
                          {t("storage.appliesWithin", {
                            seconds: config.refreshIntervalSeconds ?? 60,
                            defaultValue:
                              "Running workers apply this within {{seconds}} seconds — no restart needed.",
                          })}
                        </span>
                      )}
                      {config.batchWorkingRootSource === "database" &&
                        config.hostFileBatchWorkingRoot &&
                        config.hostFileBatchWorkingRoot !== config.batchWorkingRoot && (
                          <span style={{ color: "#92400e" }}>
                            {t("storage.hostFileStillSays", {
                              path: config.hostFileBatchWorkingRoot,
                              defaultValue:
                                "This server's own appsettings.json still contains {{path}}. The shared value above overrides it on every machine.",
                            })}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Azure Blob Configuration */}
              {config.mode === "AzureBlob" && (
                <div
                  style={{
                    paddingTop: 24,
                    borderTop: "1px solid #e5e7eb",
                    marginTop: 24,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      marginBottom: 16,
                    }}
                  >
                    Azure Blob Settings
                  </h3>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 500,
                        marginBottom: 8,
                      }}
                    >
                      Connection String *
                    </label>
                    <textarea
                      value={config.azureBlobConnectionString}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          azureBlobConnectionString: e.target.value,
                        })
                      }
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                      placeholder="DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net"
                    />
                    <p style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                      Azure Storage account connection string. The account key is masked
                      (********) for security — leave it masked to keep the current key, or
                      retype the full connection string to change it.
                    </p>
                    <p style={{ fontSize: 12, color: "#92400e", marginTop: 6 }}>
                      {t("storage.azureKeyHostLocal", {
                        defaultValue:
                          "Unlike the settings above, the access key is stored on this server only and is never copied to the other machines — it is a credential. Set it on each host that needs it.",
                      })}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: 500,
                          marginBottom: 8,
                        }}
                      >
                        Main Container
                      </label>
                      <input
                        type="text"
                        value={config.azureMainRoot}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            azureMainRoot: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                        }}
                        placeholder="main"
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: 500,
                          marginBottom: 8,
                        }}
                      >
                        Staging Container
                      </label>
                      <input
                        type="text"
                        value={config.azureStagingRoot}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            azureStagingRoot: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                        }}
                        placeholder="staging"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleTest}
                    disabled={testing || !config.azureBlobConnectionString}
                    className="secondary-btn"
                    style={{
                      marginBottom: 16,
                    }}
                  >
                    {testing
                      ? t("storage.testing", { defaultValue: "Testing..." })
                      : t("storage.testConnection", {
                          defaultValue: "Test Connection",
                        })}
                  </button>

                  {testResult && (
                    <div
                      style={{
                        padding: 16,
                        borderRadius: 6,
                        border: testResult.success
                          ? "1px solid #bbf7d0"
                          : "1px solid #fecaca",
                        backgroundColor: testResult.success
                          ? "#f0fdf4"
                          : "#fef2f2",
                        color: testResult.success ? "#166534" : "#991b1b",
                        marginBottom: 16,
                      }}
                    >
                      <p style={{ fontWeight: 600, margin: 0 }}>
                        {testResult.success
                          ? t("storage.connectionSuccess", {
                              defaultValue: "✓ Connection successful!",
                            })
                          : t("storage.connectionFailed", {
                              defaultValue: "✗ Connection failed",
                            })}
                      </p>
                      <p
                        style={{ fontSize: 14, marginTop: 6, marginBottom: 0 }}
                      >
                        {testResult.message}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* The "Configuration Guidelines" box used to live here. Every line it carried is now
                  either shown where the decision is made (the two mode cards describe themselves,
                  the status line says when a change lands) or was simply wrong — it told operators a
                  worker restart was required, which sent them looking in the wrong place when their
                  saved path did nothing. A box of general advice next to five specific notes is how
                  a settings screen becomes unreadable. */}

              {error && (
                <div
                  style={{
                    padding: 16,
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 6,
                    color: "#991b1b",
                    marginTop: 16,
                  }}
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  style={{
                    padding: 16,
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    color: "#166534",
                    marginTop: 16,
                  }}
                >
                  {success}
                </div>
              )}

              {/* Which node uses which path. One summary line by default — the table is here for
                  when the answer is "not all of them", and a screen that shows it unconditionally is
                  a screen nobody reads. The override count stays visible either way, because that is
                  the part an admin must not miss. */}
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                    {t("storage.workersTitle", {
                      defaultValue: "Which worker uses which path",
                    })}
                  </h3>

                  {workersError && (
                    <span style={{ fontSize: 12, color: "#92400e" }}>
                      {t("storage.workersUnavailable", {
                        reason: workersError,
                        defaultValue:
                          "Cannot list workers right now — {{reason}}. The settings above are unaffected.",
                      })}
                    </span>
                  )}

                  {!workersError && workers === null && (
                    <span style={{ fontSize: 12, color: "#666" }}>
                      {t("storage.loadingWorkers", { defaultValue: "Loading workers…" })}
                    </span>
                  )}

                  {!workersError && workers?.workers?.length === 0 && (
                    <span style={{ fontSize: 12, color: "#666" }}>
                      {t("storage.noWorkers", {
                        defaultValue:
                          "No worker is registered yet, so nothing is using this setting.",
                      })}
                    </span>
                  )}

                  {!workersError && workers?.workers?.length > 0 && (
                    <>
                      <span
                        style={{
                          fontSize: 12,
                          color: overrideCount > 0 ? "#92400e" : "#666",
                        }}
                      >
                        {overrideCount > 0
                          ? t("storage.workersSomeOverride", {
                              total: workers.workers.length,
                              overrides: overrideCount,
                              defaultValue:
                                "{{total}} workers · {{overrides}} use their own path and ignore the value above.",
                            })
                          : t("storage.workersAllShared", {
                              total: workers.workers.length,
                              defaultValue: "{{total}} workers, all using the shared setting.",
                            })}
                      </span>
                      <button
                        type="button"
                        onClick={() => setWorkersOpen((v) => !v)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#4f46e5",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        {workersOpen
                          ? t("common.collapse", { defaultValue: "Collapse" })
                          : t("common.expand", { defaultValue: "Expand" })}
                      </button>
                    </>
                  )}
                </div>

                {workersOpen && !workersError && workers?.workers?.length > 0 && (
                  <div style={{ overflowX: "auto", marginTop: 12 }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr style={{ textAlign: "left", color: "#4b5563" }}>
                          <th style={{ padding: "6px 8px", borderBottom: "1px solid #e5e7eb" }}>
                            {t("storage.colWorker", { defaultValue: "Worker" })}
                          </th>
                          <th style={{ padding: "6px 8px", borderBottom: "1px solid #e5e7eb" }}>
                            {t("storage.colHost", { defaultValue: "Machine" })}
                          </th>
                          <th style={{ padding: "6px 8px", borderBottom: "1px solid #e5e7eb" }}>
                            {t("storage.colPath", { defaultValue: "Batch working root" })}
                          </th>
                          <th style={{ padding: "6px 8px", borderBottom: "1px solid #e5e7eb" }}>
                            {t("storage.colSource", { defaultValue: "Source" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {workers.workers.map((w) => (
                          <tr key={w.id}>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}>
                              {w.workerName}
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}>
                              {w.hostName || "—"}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                borderBottom: "1px solid #f3f4f6",
                                fontFamily: "monospace",
                                wordBreak: "break-all",
                              }}
                            >
                              {w.effectiveBatchWorkingRoot || "—"}
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f3f4f6" }}>
                              {w.source === "override" ? (
                                <span style={{ color: "#92400e", fontWeight: 600 }}>
                                  {t("storage.ownOverride", { defaultValue: "Own override" })}
                                </span>
                              ) : (
                                <span style={{ color: "#166534" }}>
                                  {t("storage.usesShared", { defaultValue: "Shared setting" })}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 12,
                  marginTop: 24,
                  paddingTop: 24,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={fetchConfiguration}
                  disabled={loading}
                  className="secondary-btn"
                >
                  {t("storage.reset", { defaultValue: "Reset" })}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="primary-btn"
                >
                  {saving
                    ? t("storage.saving", { defaultValue: "Saving..." })
                    : t("storage.saveConfiguration", {
                        defaultValue: "Save Configuration",
                      })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      {/* Directory Browser Modal */}

      {browserOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setBrowserOpen(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              width: 520,
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Select Folder
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {browserPath && (
                  <button
                    onClick={() => { setNewFolderMode(true); setNewFolderName(""); setNewFolderError(""); }}
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      border: "1px solid #d1d5db",
                      borderRadius: 5,
                      backgroundColor: "#f9fafb",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    ➕ New Folder
                  </button>
                )}
                <button
                  onClick={() => setBrowserOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#6b7280",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Current path breadcrumb */}
            <div
              style={{
                padding: "10px 20px",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 12,
                color: "#6b7280",
                fontFamily: "monospace",
                backgroundColor: "#f9fafb",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ color: "#9ca3af" }}>Path:</span>
              <span style={{ wordBreak: "break-all" }}>
                {browserPath || "/"}
              </span>
            </div>

            {/* Directory list */}
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              {browserLoading && (
                <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>
                  Loading...
                </div>
              )}
              {browserError && (
                <div style={{ padding: "12px 20px", color: "#dc2626", fontSize: 13 }}>
                  {browserError}
                </div>
              )}
              {newFolderMode && (
                <div style={{ padding: "8px 20px", borderBottom: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      autoFocus
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") setNewFolderMode(false); }}
                      placeholder="New folder name"
                      style={{
                        flex: 1,
                        padding: "5px 8px",
                        border: "1px solid #93c5fd",
                        borderRadius: 4,
                        fontSize: 13,
                        fontFamily: "monospace",
                        outline: "none",
                      }}
                    />
                    <button
                      onClick={handleCreateFolder}
                      style={{
                        padding: "5px 10px",
                        fontSize: 12,
                        border: "none",
                        borderRadius: 4,
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Create
                    </button>
                    <button
                      onClick={() => setNewFolderMode(false)}
                      style={{
                        padding: "5px 8px",
                        fontSize: 12,
                        border: "1px solid #d1d5db",
                        borderRadius: 4,
                        backgroundColor: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {newFolderError && (
                    <p style={{ fontSize: 11, color: "#dc2626", margin: "4px 0 0" }}>{newFolderError}</p>
                  )}
                </div>
              )}
              {!browserLoading && !browserError && (
                <>
                  {browserParent !== null && browserParent !== undefined && (
                    <div
                      onClick={() => navigateTo(browserParent)}
                      style={{
                        padding: "8px 20px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontSize: 13,
                        color: "#374151",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                    >
                      <span style={{ fontSize: 16 }}>⬆️</span>
                      <span style={{ fontFamily: "monospace" }}>..</span>
                    </div>
                  )}
                  {browserEntries.length === 0 && (
                    <div style={{ padding: "12px 20px", color: "#9ca3af", fontSize: 13 }}>
                      No subdirectories found
                    </div>
                  )}
                  {browserEntries.map((entry) => (
                    <div
                      key={entry.path}
                      style={{
                        padding: "8px 20px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontSize: 13,
                        color: "#374151",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                    >
                      <span
                        style={{ fontSize: 16, flexShrink: 0 }}
                        onClick={() => navigateTo(entry.path)}
                      >
                        📁
                      </span>
                      <span
                        style={{ flex: 1, fontFamily: "monospace", wordBreak: "break-all" }}
                        onClick={() => navigateTo(entry.path)}
                      >
                        {entry.name}
                      </span>
                      <button
                        onClick={() => selectBrowserPath(entry.path)}
                        style={{
                          padding: "3px 10px",
                          fontSize: 11,
                          border: "1px solid #d1d5db",
                          borderRadius: 4,
                          backgroundColor: "#fff",
                          cursor: "pointer",
                          flexShrink: 0,
                          color: "#374151",
                        }}
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <button
                onClick={() => browserPath && selectBrowserPath(browserPath)}
                disabled={!browserPath}
                className="primary-btn"
                style={{ fontSize: 13 }}
              >
                Use Current Folder
              </button>
              <button
                onClick={() => setBrowserOpen(false)}
                className="secondary-btn"
                style={{ fontSize: 13 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
